// @flow

import React, { useEffect } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { compose, setDisplayName } from 'recompose';
import {
  injectSheet,
  BaseText,
  Header2,
  type InjectSheetProvidedProps,
  type ThemeType
} from '@catalytic/catalytic-ui';
import WebformLogo from './WebformLogo';
import successUrl from '../images/success.svg';
import getReferrerUrl from '../utils/getReferrerUrl';

const REF_SUCCESS = 'ref-success';

export const Style = (theme: ThemeType) => ({
  logo: {
    backgroundImage: `url(${successUrl})`,
    minHeight: '11rem',
    minWidth: '11rem',
    margin: '2rem 0 1rem',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center'
  },
  description: {
    ...theme.mixins.formMarginBottom,
    color: theme.colors.slateGrey,
    marginTop: '.5rem',
    textAlign: 'center'
  }
});

type Props = InjectSheetProvidedProps & {
  displayName?: string,
  hideHeader: boolean,
  intl: Object,
  params: URLSearchParams,
  runID?: string,
  sessionStorageKey?: string,
  src?: string
};

export const WebformSuccess = ({
  classes,
  displayName,
  hideHeader,
  params,
  runID,
  sessionStorageKey,
  src
}: Props) => {
  useEffect(() => {
    // Remove session data
    if (sessionStorageKey) {
      window.localStorage.removeItem(sessionStorageKey);
    }
  }, [sessionStorageKey]);

  const referrer = params && params.get(REF_SUCCESS);

  if (referrer) {
    const url = getReferrerUrl({ key: REF_SUCCESS, referrer, runID });

    // Disable xss lint because URL interface will encode by default.
    // See `getReferrerUrl` utility function.
    // https://developer.mozilla.org/en-US/docs/Web/API/URL
    if (url) {
      window.location.href = url; // eslint-disable-line xss/no-location-href-assign
    }

    return null;
  } else {
    return (
      <>
        {!hideHeader && <WebformLogo src={src} alt={displayName} />}
        <div className={classes.logo} />
        <Header2>
          <FormattedMessage id="webform.success" defaultMessage="Success!" />
        </Header2>
        <BaseText className={classes.description}>
          <FormattedMessage
            id="webform.success.description"
            defaultMessage="Thank you for completing the form."
          />
        </BaseText>
      </>
    );
  }
};
WebformSuccess.displayName = 'WebformSuccess';

export default compose(
  injectIntl,
  injectSheet(Style),
  setDisplayName('WebformSuccess')
)(WebformSuccess);
